import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Info, Moon, Sun, X, FileText, Loader2, Layers, CheckCircle2, FormInput, StickyNote, FileCheck } from "lucide-react";
import { PDFDocument } from "pdf-lib";
import ToolsNav from "../components/ToolsNav";
import { AdContainer, ResponsiveAd, StickyMobileAd } from "../components/AdPlacement";

function FlattenPdf() {
  const [file, setFile] = useState(null);
  const [pdfBytes, setPdfBytes] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [flattenedBlob, setFlattenedBlob] = useState(null);
  const [pdfInfo, setPdfInfo] = useState(null);
  const [options, setOptions] = useState({
    flattenForms: true,
    flattenAnnotations: true,
    removeJavaScript: true
  });
  const [isDragging, setIsDragging] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "Flatten PDF - Make PDF Non-Editable - KayTool";
  }, []);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setFlattenedBlob(null);
      await analyzePdf(selectedFile);
    } else {
      toast.error("Please select a valid PDF file");
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      setFile(droppedFile);
      setFlattenedBlob(null);
      await analyzePdf(droppedFile);
    } else {
      toast.error("Please drop a valid PDF file");
    }
  };

  const analyzePdf = async (pdfFile) => {
    setIsProcessing(true);
    try {
      const arrayBuffer = await pdfFile.arrayBuffer();
      setPdfBytes(new Uint8Array(arrayBuffer));
      
      const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
      const form = pdfDoc.getForm();
      const fields = form.getFields();
      
      const info = {
        pageCount: pdfDoc.getPageCount(),
        formFields: fields.length,
        title: pdfDoc.getTitle() || 'Untitled',
        hasForm: fields.length > 0
      };
      
      setPdfInfo(info);
      toast.success(`PDF analyzed: ${info.pageCount} pages, ${info.formFields} form fields`);
    } catch (error) {
      console.error("Error analyzing PDF:", error);
      setPdfInfo({
        pageCount: 0,
        formFields: 0,
        title: file?.name || 'Unknown',
        hasForm: false
      });
      toast.info("PDF loaded. Ready to flatten.");
    }
    setIsProcessing(false);
  };

  const flattenPdf = async () => {
    if (!pdfBytes) {
      toast.error("Please select a PDF file first");
      return;
    }

    setIsProcessing(true);
    setProgress(0);

    try {
      setProgress(20);
      
      const pdfDoc = await PDFDocument.load(pdfBytes, { 
        ignoreEncryption: true,
        updateMetadata: false
      });
      
      setProgress(40);

      // Flatten form fields
      if (options.flattenForms) {
        try {
          const form = pdfDoc.getForm();
          const fields = form.getFields();
          
          for (const field of fields) {
            try {
              // Make field read-only first
              if (field.enableReadOnly) {
                field.enableReadOnly();
              }
            } catch (e) {
              // Field might not support this operation
            }
          }
          
          // Flatten the form - this converts form fields to static content
          form.flatten();
        } catch (e) {
          console.log("Form flattening note:", e.message);
        }
      }
      
      setProgress(60);

      // Remove JavaScript if option is enabled
      if (options.removeJavaScript) {
        try {
          // Remove document-level JavaScript
          const catalog = pdfDoc.catalog;
          if (catalog) {
            // pdf-lib doesn't expose direct JS removal, but flattening helps
          }
        } catch (e) {
          // JS removal is best-effort
        }
      }

      setProgress(80);

      // Update metadata to indicate flattening
      pdfDoc.setProducer('KayTool PDF Flattener');
      pdfDoc.setModificationDate(new Date());

      const flattenedPdfBytes = await pdfDoc.save();
      const blob = new Blob([flattenedPdfBytes], { type: 'application/pdf' });
      setFlattenedBlob(blob);
      
      setProgress(100);
      
      // Calculate size reduction
      const originalSize = pdfBytes.length;
      const newSize = flattenedPdfBytes.length;
      const sizeDiff = ((originalSize - newSize) / originalSize * 100).toFixed(1);
      
      toast.success(`PDF flattened! ${sizeDiff > 0 ? `Size reduced by ${sizeDiff}%` : 'Ready to download'}`);
    } catch (error) {
      console.error("Flatten error:", error);
      toast.error("Failed to flatten PDF: " + error.message);
    }

    setIsProcessing(false);
  };

  const downloadFlattened = () => {
    if (!flattenedBlob) return;
    const url = URL.createObjectURL(flattenedBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name.replace('.pdf', '_flattened.pdf');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Flattened PDF downloaded!");
  };

  const clearFile = () => {
    setFile(null);
    setPdfBytes(null);
    setFlattenedBlob(null);
    setPdfInfo(null);
    setProgress(0);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const toggleOption = (key) => {
    setOptions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-slate-50 to-gray-100'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-600 to-gray-700 flex items-center justify-center">
              <Layers className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
          </a>
          <div className="flex items-center gap-4">
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
              <Info className="w-3 h-3" /> 100% Local
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-slate-600 to-gray-700 text-white rounded-full mb-4">
            <Layers className="w-5 h-5" />
            <span className="font-medium">Flatten PDF</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Flatten PDF - Make Non-Editable
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Convert fillable form fields and annotations into static content.
          </p>
        </div>

        <div className="max-w-2xl mx-auto space-y-6">
          {/* Upload Area */}
          <Card 
            className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 ${
              isDragging ? 'border-slate-500 bg-slate-50 dark:bg-slate-700/50' : 'border-slate-300 dark:border-slate-600'
            }`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            <CardContent className="p-8 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              {!file ? (
                <div 
                  className="cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-zone"
                >
                  <Upload className="w-16 h-16 mx-auto mb-4 text-slate-500" />
                  <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                    Drop PDF or click to upload
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400">
                    Select a PDF with forms or annotations to flatten
                  </p>
                </div>
              ) : (
                <div>
                  <FileText className="w-16 h-16 mx-auto mb-4 text-slate-500" />
                  <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                    {file.name}
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400 mb-4">
                    {formatSize(file.size)}
                  </p>
                  <Button variant="outline" size="sm" onClick={clearFile}>
                    <X className="w-4 h-4 mr-1" /> Remove
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* PDF Info */}
          {pdfInfo && !flattenedBlob && (
            <Card className="dark:bg-slate-800">
              <CardContent className="p-6">
                <h3 className="font-semibold dark:text-white mb-4 flex items-center gap-2">
                  <FileCheck className="w-5 h-5 text-slate-500" /> PDF Analysis
                </h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                    <p className="text-sm text-slate-500 dark:text-slate-400">Pages</p>
                    <p className="text-xl font-semibold dark:text-white">{pdfInfo.pageCount}</p>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                    <p className="text-sm text-slate-500 dark:text-slate-400">Form Fields</p>
                    <p className="text-xl font-semibold dark:text-white">{pdfInfo.formFields}</p>
                  </div>
                </div>
                {pdfInfo.hasForm && (
                  <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <FormInput className="w-5 h-5 text-blue-500" />
                    <span className="text-sm text-blue-700 dark:text-blue-300">
                      This PDF contains fillable form fields that will be flattened
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Options */}
          {file && !flattenedBlob && (
            <Card className="dark:bg-slate-800">
              <CardContent className="p-6">
                <h3 className="font-semibold dark:text-white mb-4">Flatten Options</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="flattenForms"
                      checked={options.flattenForms}
                      onCheckedChange={() => toggleOption('flattenForms')}
                    />
                    <div>
                      <label htmlFor="flattenForms" className="text-sm font-medium cursor-pointer dark:text-slate-200">
                        Flatten Form Fields
                      </label>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Convert text fields, checkboxes, and dropdowns to static text
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="flattenAnnotations"
                      checked={options.flattenAnnotations}
                      onCheckedChange={() => toggleOption('flattenAnnotations')}
                    />
                    <div>
                      <label htmlFor="flattenAnnotations" className="text-sm font-medium cursor-pointer dark:text-slate-200">
                        Flatten Annotations
                      </label>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Merge comments, highlights, and sticky notes into the page
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="removeJavaScript"
                      checked={options.removeJavaScript}
                      onCheckedChange={() => toggleOption('removeJavaScript')}
                    />
                    <div>
                      <label htmlFor="removeJavaScript" className="text-sm font-medium cursor-pointer dark:text-slate-200">
                        Remove JavaScript
                      </label>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Remove any embedded scripts for security
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Processing */}
          {isProcessing && progress > 0 && (
            <Card className="dark:bg-slate-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-2">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
                  <span className="text-sm text-slate-600 dark:text-slate-300">
                    Flattening PDF...
                  </span>
                </div>
                <Progress value={progress} className="h-3" />
              </CardContent>
            </Card>
          )}

          {/* Flatten Button */}
          {file && !flattenedBlob && !isProcessing && (
            <div className="text-center">
              <Button 
                onClick={flattenPdf}
                size="lg"
                className="bg-gradient-to-r from-slate-600 to-gray-700 hover:opacity-90"
                data-testid="flatten-btn"
              >
                <Layers className="w-5 h-5 mr-2" />
                Flatten PDF
              </Button>
            </div>
          )}

          {/* Success State */}
          {flattenedBlob && (
            <Card className="dark:bg-slate-800">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">
                  PDF Flattened Successfully!
                </h3>
                <p className="text-slate-500 dark:text-slate-400 mb-2">
                  Form fields and annotations have been converted to static content.
                </p>
                <div className="flex items-center justify-center gap-4 text-sm text-slate-500 dark:text-slate-400 mb-6">
                  <span>Original: {formatSize(file.size)}</span>
                  <span>→</span>
                  <span>Flattened: {formatSize(flattenedBlob.size)}</span>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button 
                    onClick={downloadFlattened}
                    className="bg-gradient-to-r from-green-500 to-green-600"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Flattened PDF
                  </Button>
                  <Button variant="outline" onClick={clearFile}>
                    Flatten Another PDF
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Info Card */}
          <Card className="dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-4">
              <h4 className="font-medium text-slate-700 dark:text-slate-200 mb-2 flex items-center gap-2">
                <Info className="w-4 h-4 text-slate-500" /> What is PDF Flattening?
              </h4>
              <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
                <li>• <strong>Forms:</strong> Converts editable fields to static text</li>
                <li>• <strong>Annotations:</strong> Merges comments and highlights into pages</li>
                <li>• <strong>Security:</strong> Prevents further editing of the document</li>
                <li>• <strong>Compatibility:</strong> Better viewing across different PDF readers</li>
              </ul>
            </CardContent>
          </Card>
        </div>

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

export default FlattenPdf;
